import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar } from "recharts";
import { toast } from "sonner";

interface ABTestData {
  date: string;
  showcase_assignments: number;
  onboarding_assignments: number;
  showcase_signups: number;
  onboarding_signups: number;
}

type ViewMode = "assignments" | "signups" | "conversion";

const AdminABTestAnalytics = () => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>("signups");
  const [data, setData] = useState<ABTestData[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<"30" | "60" | "90">("30");

  useEffect(() => {
    fetchABTestData();
  }, [timeRange]);

  const fetchABTestData = async () => {
    try {
      setLoading(true);

      const days = parseInt(timeRange);
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data: analyticsData, error } = await (supabase.rpc as any)("get_ab_test_analytics_for_admin", {
        p_start_date: startDate.toISOString(),
        p_end_date: new Date().toISOString(),
      });

      if (error) throw error;

      const transformedData: ABTestData[] = (Array.isArray(analyticsData) ? analyticsData : []).map((row: any) => ({
        date: row.date,
        showcase_assignments: Number(row.showcase_assignments) || 0,
        onboarding_assignments: Number(row.onboarding_assignments) || 0,
        showcase_signups: Number(row.showcase_signups) || 0,
        onboarding_signups: Number(row.onboarding_signups) || 0,
      }));

      setData(transformedData);
    } catch (error) {
      console.error("[AdminABTestAnalytics] Error fetching data:", error);
      toast.error(t('components.admin_ab_test.toasts.load_error'));
    } finally {
      setLoading(false);
    }
  };

  const getTotals = () => {
    const totals = data.reduce(
      (acc, row) => ({
        showcaseAssignments: acc.showcaseAssignments + row.showcase_assignments,
        onboardingAssignments: acc.onboardingAssignments + row.onboarding_assignments,
        showcaseSignups: acc.showcaseSignups + row.showcase_signups,
        onboardingSignups: acc.onboardingSignups + row.onboarding_signups,
      }),
      { showcaseAssignments: 0, onboardingAssignments: 0, showcaseSignups: 0, onboardingSignups: 0 }
    );

    const showcaseRate = totals.showcaseAssignments > 0
      ? ((totals.showcaseSignups / totals.showcaseAssignments) * 100).toFixed(2)
      : "0.00";
    const onboardingRate = totals.onboardingAssignments > 0
      ? ((totals.onboardingSignups / totals.onboardingAssignments) * 100).toFixed(2)
      : "0.00";

    return { ...totals, showcaseRate, onboardingRate };
  };

  const renderChart = () => {
    if (viewMode === "conversion") {
      // Compute cumulative conversion rates
      const conversionData = data.map((row) => ({
        date: row.date,
        showcase_rate: row.showcase_assignments > 0
          ? parseFloat(((row.showcase_signups / row.showcase_assignments) * 100).toFixed(2))
          : 0,
        onboarding_rate: row.onboarding_assignments > 0
          ? parseFloat(((row.onboarding_signups / row.onboarding_assignments) * 100).toFixed(2))
          : 0,
      }));

      return (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={conversionData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="date" className="text-xs" tick={{ fontSize: 12 }} />
            <YAxis className="text-xs" tick={{ fontSize: 12 }} unit="%" />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
              formatter={(value: number) => `${value}%`}
            />
            <Legend />
            <Line type="monotone" dataKey="showcase_rate" stroke="#3b82f6" strokeWidth={2} name="Showcase %" dot={{ fill: '#3b82f6', r: 3 }} />
            <Line type="monotone" dataKey="onboarding_rate" stroke="#10b981" strokeWidth={2} name="Onboarding %" dot={{ fill: '#10b981', r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (viewMode === "signups") {
      return (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="date" className="text-xs" tick={{ fontSize: 12 }} />
            <YAxis className="text-xs" tick={{ fontSize: 12 }} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
            />
            <Legend />
            <Bar dataKey="showcase_signups" fill="#3b82f6" name="Showcase Signups" />
            <Bar dataKey="onboarding_signups" fill="#10b981" name="Onboarding Signups" />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    // assignments view  
    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis dataKey="date" className="text-xs" tick={{ fontSize: 12 }} />
          <YAxis className="text-xs" tick={{ fontSize: 12 }} />
          <Tooltip
            contentStyle={{
              backgroundColor: 'hsl(var(--background))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '8px',
            }}
          />
          <Legend />
          <Bar dataKey="showcase_assignments" fill="#3b82f6" name="Showcase Visitors" />
          <Bar dataKey="onboarding_assignments" fill="#10b981" name="Onboarding Visitors" />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const totals = getTotals();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <CardTitle>🧪 A/B Test: Landing Page</CardTitle>
          <div className="flex gap-2">
            <Select value={timeRange} onValueChange={(value: any) => setTimeRange(value)}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder={t('components.admin_ab_test.time_range')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">{t('components.admin_ab_test.last_30_days')}</SelectItem>
                <SelectItem value="60">{t('components.admin_ab_test.last_60_days')}</SelectItem>
                <SelectItem value="90">{t('components.admin_ab_test.last_90_days')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={viewMode} onValueChange={(value) => setViewMode(value as ViewMode)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder={t('components.admin_ab_test.select_view')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="assignments">{t('components.admin_ab_test.visitor_assignments')}</SelectItem>
                <SelectItem value="signups">{t('components.admin_ab_test.signups_by_variant')}</SelectItem>
                <SelectItem value="conversion">{t('components.admin_ab_test.conversion_rate')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-[300px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : data.length === 0 ? (
          <div className="flex items-center justify-center h-[300px] text-muted-foreground">{t('components.admin_ab_test.no_data')}</div>
        ) : (
          <div>
            {/* Summary stats */}
            <div className="mb-4 grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Showcase</p>
                <p className="text-2xl font-bold">{totals.showcaseSignups} <span className="text-sm font-normal text-muted-foreground">signups</span></p>
                <p className="text-xs text-muted-foreground">{totals.showcaseAssignments} visitors · {totals.showcaseRate}% conv.</p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">Onboarding</p>
                <p className="text-2xl font-bold">{totals.onboardingSignups} <span className="text-sm font-normal text-muted-foreground">signups</span></p>
                <p className="text-xs text-muted-foreground">{totals.onboardingAssignments} visitors · {totals.onboardingRate}% conv.</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Winner</p>
                <p className="text-2xl font-bold">
                  {parseFloat(totals.showcaseRate) > parseFloat(totals.onboardingRate)
                    ? "Showcase"
                    : parseFloat(totals.onboardingRate) > parseFloat(totals.showcaseRate)
                    ? "Onboarding"
                    : "Tied"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {Math.abs(parseFloat(totals.showcaseRate) - parseFloat(totals.onboardingRate)).toFixed(2)}% difference
                </p>
              </div>
            </div>
            {renderChart()}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminABTestAnalytics;
